import React, { useEffect, useState } from "react";
import { FaPlus, FaInfoCircle, FaCheck, FaTimes } from "react-icons/fa";
import { Modal, FormControl } from 'react-bootstrap';
import { 
    UpdateSelectionModifierNameById,
    UpdateSelectionModifierDescriptionById,
 } from '../../../../../../crud/menu_api/selection_modifiers';
import MiniLoadingSpinner from "../../../../../../components/Spinners/MiniLoadingSpinner";
import { toast } from 'react-toastify';
import './ModifierDetail.scss';
import OptionsTable from './components/OptionsTable/OptionsTable';
import ItemsTable from "./components/ItemsTable/ItemsTable";
import CreateOptionModal from './components/CreateOptionModal/CreateOptionModal';

interface Modifier {
    modifier_id: number;
    modifier_name: string;
    modifier_description?: string;
    local_id: number;
    items_affected: Array<{
        item_id: number;
        item_name: string;
    }>;
    options_included: Array<{
        option_id: number;
        option_name: string;
    }>;
}

interface GroupDetailProps {
  show: boolean;
  setShow: (show: boolean) => void;
  mod: Modifier;
  data: Modifier[];
  setData: (data: Modifier[]) => void;
}

const GroupDetail: React.FC<GroupDetailProps> = ({ show, setShow, mod, data, setData }) => {
  const [loading, setLoading] = useState(false);

  // Estado para manejar el título en tiempo real
  const [currentName, setCurrentName] = useState<string>(mod.modifier_name);
  const [draftName, setDraftName] = useState<string>(mod.modifier_name);
  const [currentDescription, setCurrentDescription] = useState<string>(mod.modifier_description || '');
  const [draftDescription, setDraftDescription] = useState<string>(mod.modifier_description || '');
  const [viewMode, setViewMode] = useState<'options' | 'associations'>('options');

  // Estado para el modal de crear opción
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Estado para el modal de vincular ítem (controlado desde el padre)
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);

  const handleOptionCreated = () => {
    setRefreshTrigger(prev => prev + 1); // Trigger refresh in OptionsTable
    setShowCreateModal(false);
  };

// Cuando cambia el prop mod.modifier_name (por ejemplo desde fuera), reseteamos ambos estados
  useEffect(() => {
    setCurrentName(mod.modifier_name);
    setDraftName(mod.modifier_name);
    setCurrentDescription(mod.modifier_description || '');
    setDraftDescription(mod.modifier_description || '');
  }, [mod.modifier_name, mod.modifier_description]);

  const persistName = () => {
    const trimmed = draftName.trim();
    if (trimmed === "") {
      toast.error('El nombre del grupo modificador no puede estar vacío.');
      return;
    }
    if (trimmed === currentName) return; // no changes
    setLoading(true);
    UpdateSelectionModifierNameById({ modifier_name: trimmed }, mod.modifier_id)
      .then(() => {
        toast.success('Nombre del grupo modificador actualizado.');
        // Actualizar datos en el padre
        const updatedData = data.map(modifier => 
          modifier.modifier_id === mod.modifier_id 
            ? { ...modifier, modifier_name: trimmed } 
            : modifier
        );
        setData(updatedData);
        setCurrentName(trimmed);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error actualizando el nombre del grupo modificador', error);
        const message = error.response?.data?.message || 'Error al actualizar el nombre del grupo modificador';
        toast.error(message);
        setDraftName(currentName); // revert
        setLoading(false);
      });
  };

  const persistDescription = () => {
    const trimmed = draftDescription.trim();
    if (trimmed === currentDescription) return; // no changes
    
    setLoading(true);
    UpdateSelectionModifierDescriptionById({ modifier_description: trimmed }, mod.modifier_id)
      .then(() => {
        toast.success('Descripción del modificador actualizada.');
        const updatedData = data.map(modifier => 
          modifier.modifier_id === mod.modifier_id 
            ? { ...modifier, modifier_description: trimmed } 
            : modifier
        );
        setData(updatedData);
        setCurrentDescription(trimmed);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error actualizando la descripción del modificador', error);
        const message = error.response?.data?.message || 'Error al actualizar la descripción del modificador';
        toast.error(message);
        setDraftDescription(currentDescription); // revert
        setLoading(false);
      });
  };

  const renderViewSelector = () => {
    const affectedCount = mod.items_affected?.length || 0;
    return (
      <div className="gd-toggle">
        <button
          type="button"
          className={`pill ${viewMode === 'options' ? 'active' : ''}`}
          onClick={() => setViewMode('options')}
        >
          Opciones
        </button>
        <button
          type="button"
          className={`pill ${viewMode === 'associations' ? 'active' : ''}`}
          onClick={() => setViewMode('associations')}
        >
          Vinculado a <span className="count">{affectedCount}</span>
        </button>
        <div className="pill-glider" aria-hidden="true" />
      </div>
    );
  };

  const renderModifierOptions = () => (
    <div className="gd-section">
      <div className="gd-section-header">
        <div className="section-info">
          <h4>Opciones del modificador</h4>
          <p className="hint">Estas son las opciones que el cliente podrá elegir para personalizar el producto.</p>
        </div>
        <button className="action-btn" onClick={() => setShowCreateModal(true)}>
          <FaPlus /> Añadir opción
        </button>
      </div>
      <OptionsTable
        mod={mod}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );

  const renderItemAssociations = () => (
    <div className="gd-section">
      <div className="gd-section-header">
        <div className="section-info">
          <h4>Productos vinculados</h4>
          <p className="hint">Estos productos muestran este modificador como opción para el cliente.</p>
        </div>
        <button className="action-btn" onClick={() => setShowCreateItemModal(true)}>
          <FaPlus /> Vincular ítem al grupo
        </button>
      </div>
      <div className="table-wrapper table-wrapper-large">
        <ItemsTable 
          mod={mod} 
          showCreateModal={showCreateItemModal}
          setShowCreateModal={setShowCreateItemModal}
        />
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      className="selection-modifiers-details-modal"
      scrollable
      fullscreen="sm-down"
    >
      <Modal.Header className="gd-header">
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Configuración de modificador</h2>
        </div>
        <button type="button" className="modal-close-button" aria-label="Cerrar" onClick={() => setShow(false)}>
          <FaTimes />
        </button>
      </Modal.Header>

      {loading && <MiniLoadingSpinner />}

      {!loading && (
        <Modal.Body className="gd-body">
          <div className="gd-inline-name">
            <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
              <FormControl
                type="text"
                value={draftName}
                onChange={(e) => { setDraftName(e.target.value); }}
                onKeyDown={(e) => { 
                  if (e.key === 'Escape') { setDraftName(currentName); } 
                  if (e.key === 'Enter' && draftName.trim() !== '' && draftName.trim() !== currentName.trim()) { persistName(); } 
                }}
                className="gd-name-input always-edit"
                maxLength={60}
                placeholder="Nombre del modificador"
                aria-label="Nombre del modificador"
                autoFocus
              />
              {draftName.trim() !== '' && draftName.trim() !== currentName.trim() && (
                <button type="button" className="btn form-button-create" onClick={persistName} aria-label="Guardar nombre del modificador">
                  <FaCheck style={{ marginRight:6 }} /> Guardar
                </button>
              )}
            </div>
          </div>
          {renderViewSelector()}
          {viewMode === 'options' ? renderModifierOptions() : renderItemAssociations()}
        </Modal.Body>
      )}

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button type="button" className="btn form-button-cancel" onClick={() => setShow(false)}>Cerrar</button>
        </div>
      </Modal.Footer>

      {showCreateModal && (
        <CreateOptionModal
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          mod={mod}
          setOptions={setOptions}
          options={options}
          onOptionCreated={handleOptionCreated}
        />
      )}
    </Modal>
  );
};

export default GroupDetail;
